












































import Vue from 'vue';
import Component from 'vue-class-component';

// Shared types
import { Company, MinimumWaitingTime } from '@coldtec/cos-types';

// Store types
import { CompanyGetter } from '@/store/store.types';

// The base for every input component
import InputBase from './Base.vue';

// The datepicker is always shown in a modal
import Modal from '@/components/modal/Modal.vue';

@Component
export default class InputDate extends InputBase<string> {

    public $refs!: {
        modal: Modal;
        datePicker: Vue;
    };

    /**
     *  Used for observing the date picker's body, so our custom
     *  arrows are rendered when the user changes month or year.
     */
    private observer: MutationObserver | null = null;

    /**
     *  Getter and setter to forward the v-model
     *  binding to the date picker component.
     */
    private get date(): string | null {

        return this.newValue;

    }

    private set date(date: string | null) {

        this.close(date);

    }

    /**
     *  Used to get the currently selected date,
     *  formatted according to the Dutch locale.
     */
    private get formattedDate(): string | null {

        if (!this.date) {

            return null;

        }

        return new Intl.DateTimeFormat('nl-NL').format(new Date(this.date));

    }

    /**
     *  Used to get the company for which the tool is used.
     */
    private get company(): Company {

        return this.$store.getters[CompanyGetter.DETAILS];

    }

    /**
     *
     *  Used to get the fact if the forwarded date is allowed.
     *
     *  @param dateString The string of the date to check, YYYY-MM-DD
     *
     */
    private allowedDates(dateString: string): boolean {

        let allowed = true;

        /**
         *  It's always allowed if the company doesn't have a waiting time.
         */
        const minimumWaitingTime = this.company.minimum_waiting_time as MinimumWaitingTime | undefined;

        if (!minimumWaitingTime) {

            return allowed;

        }

        /**
         *  Compute the date from which the company is available,
         *  based on the provided date and today.
         */
        const date = new Date(dateString);
        const today = new Date();
        let availableFromDate = new Date();

        /**
         *
         *  Compute the available from date.
         *
         *  (i) Order and no early returns are important,
         *  because the larger block of waiting time (week
         *  or month) must always be leading
         *
         */
        if (minimumWaitingTime.days) {

            availableFromDate = new Date(today.setDate(today.getDate() + minimumWaitingTime.days));

        }

        if (minimumWaitingTime.weeks) {

            availableFromDate = new Date(today.setDate(today.getDate() + (minimumWaitingTime.weeks * 7)));

        }

        if (minimumWaitingTime.months) {

            availableFromDate = new Date(today.setMonth(today.getMonth() + minimumWaitingTime.months));

        }

        /**
         *  The date is allowed if it's on or further
         *  than the date from which the company is
         *  available again.
         */
        return date >= availableFromDate;

    }

    /**
     *  Used to open the date picker (modal), including the updating of
     *  the navigation buttons and keeping them in our style.
     */
    private open(): void {

        this.$refs.modal.open();

        /**
         *  Give the DOM a tick to render the modal with the date picker.
         */
        this.$nextTick(() => {

            /**
             *  Update the navigation buttons to give them our style.
             */
            this._updateNavButtons();

            /**
             *  Keep updating the buttons on every mutation, ensuring
             *  our styles keeps being applied when the user selects
             *  another month or year.
             */
            const pickerBody: HTMLElement | null = this.$refs.datePicker.$el.querySelector('.v-picker__body');

            if (!pickerBody) {

                return;

            }

            this.observer = new MutationObserver(this._updateNavButtons.bind(this));

            this.observer.observe(pickerBody, { attributes: true, childList: true });

        });

    }

    /**
     *
     *  Used to close the date picker, including the disconnecting
     *  of the observer and emitting the selected date.
     *
     *  @param date The selected date
     *
     */
    private close(date: string | null): void {

        this.$refs.modal.close();

        this.observer?.disconnect();

        this.onInput(date);

    }

    /**
     *  Used to give the date picker's navigation buttons our style.
     */
    private _updateNavButtons(): void {

        const previous: HTMLElement | null = this.$refs.datePicker.$el.querySelector('[aria-label="Previous month"]');
        const next: HTMLElement | null = this.$refs.datePicker.$el.querySelector('[aria-label="Next month"]');

        if (!previous || !next) {

            return;

        }

        this._updateNavButton(
            previous,
            '<path d="M10.845 4.17H2.503l2.745-2.744c.158-.157.247-.37.247-.592S5.405.399 5.248.243c-.325-.323-.851-.323-1.176 0L.492 3.828A1.67 1.67 0 0 0 0 5.004h0a1.67 1.67 0 0 0 .492 1.168l3.579 3.586c.212.21.519.291.807.213s.512-.304.588-.592-.007-.595-.219-.805L2.503 5.838h8.343c.461 0 .834-.373.834-.834s-.374-.834-.834-.834z" />',
        );

        this._updateNavButton(
            next,
            '<path d="M.834 4.17h8.343L6.432 1.427c-.158-.157-.247-.37-.247-.592s.089-.436.247-.592c.325-.323.851-.323 1.176 0l3.579 3.586a1.67 1.67 0 0 1 .483 1.011l.009.165a1.67 1.67 0 0 1-.492 1.168L7.608 9.758c-.212.21-.519.291-.807.213s-.512-.304-.588-.592.007-.595.219-.805l2.745-2.735H.834C.374 5.838 0 5.465 0 5.004s.374-.834.834-.834z" />',
        );

    }

    /**
     *
     *  Used to update the style of the provided navigation button.
     *
     *  (i) Replacing Vuetify's button with our SVG and
     *  applying only our own class / styling.
     *
     *  @param button The HTML element of the button to update
     *  @param path The path to add to the SVG element that is added to the button
     *
     */
    private _updateNavButton(button: HTMLElement, path: string): void {

        /**
         *  Clean the button and append the SVG.
         */
        while (button.firstChild) {

            button.firstChild.remove();

        }

        button.innerHTML = `<svg class="date__nav-icon" xmlns="http://www.w3.org/2000/svg" width="12" height="10">${ path }</svg>`;

        /**
         *  Remove all other classes (and thus styling) and apply our own.
         */
        button.className = 'date__nav-btn';

    }

}
